import { useCallback, useEffect, useRef, useState } from 'react';
import { documentService } from '../services/documentService';
import { logger } from '../utils/logger';
import { userErrorMessage } from '../utils/userError';

/**
 * Multi-segment composer recorder — record MANY clips in one composition, each
 * transcribed in the BACKGROUND so the user can immediately start the next one
 * ("recording multiple audios in one go", #173 Full Tool Parity).
 *
 * Produces exactly the segment shape the Main Office composer produces, so it
 * feeds <ChatInput>'s VoiceSegmentChip and `composeSnapshot()` unchanged:
 *   { id, blob, blobUrl, duration, mimeType, status, audioUrl, gcsPath,
 *     transcription, showTranscription, error }
 *
 * WHY A SEPARATE HOOK (rather than reusing MainOfficeView's recorder): the Main
 * Office copy is inseparably entangled with per-conversation DRAFT + origin
 * binding (a clip whose transcription resolves after the user switches
 * conversations patches the ORIGIN conversation's persisted draft, and can flush
 * a queued "Guaranteed Delivery" send) — the most heavily bug-fixed path in the
 * app. A Bucket Day Focus workspace is bound to ONE conversation and unmounts on
 * close, so none of that machinery applies; extracting it would mean rewriting
 * that path for zero user-visible gain. `useVoiceRecorder` (used by the message
 * edit flow) is not a fit either: its stopAndTranscribe() AWAITS transcription,
 * which would block the user between clips.
 *
 * Recording is INDEPENDENT of typed text — the caller keeps its own `input`
 * state, so typing never blocks recording (the PO's explicit complaint).
 */
// `conversationId` is optional and purely for ENERGY ATTRIBUTION (#377): it
// rides along to /api/audio/transcribe so the clip's transcription cost lands on
// the Energy Receipt's Transcription line for the right conversation. Defaulting
// to null keeps every existing caller byte-identical.
export function useComposerVoiceSegments(conversationId = null) {
    const [segments, setSegments] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const startedAtRef = useRef(0);
    const timerRef = useRef(null);
    // Live mirror so the unmount cleanup can revoke blob URLs without making the
    // effect depend on `segments` (which would re-run it on every change).
    const segmentsRef = useRef(segments);
    useEffect(() => { segmentsRef.current = segments; }, [segments]);

    const teardownRecorder = useCallback(() => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        recorderRef.current = null;
        chunksRef.current = [];
        setIsRecording(false);
        setRecordingDuration(0);
    }, []);

    const start = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : 'audio/webm';
            const recorder = new MediaRecorder(stream, { mimeType });
            chunksRef.current = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            streamRef.current = stream;
            recorderRef.current = recorder;
            recorder.start();
            startedAtRef.current = Date.now();
            setIsRecording(true);
            setRecordingDuration(0);
            timerRef.current = setInterval(() => setRecordingDuration((p) => p + 1), 1000);
        } catch (err) {
            logger.error('VOICE', 'Failed to start recording:', err);
            teardownRecorder();
        }
    }, [teardownRecorder]);

    /** Stop + drop the capture. No segment is queued. */
    const discard = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            try { recorder.stop(); } catch (_) { /* ignore */ }
        }
        teardownRecorder();
    }, [teardownRecorder]);

    /**
     * Stop, QUEUE the clip immediately (playable from its local blob URL) and
     * transcribe in the background. Returns as soon as the blob is captured, so
     * the user can start the next recording without waiting.
     */
    const accept = useCallback(async () => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === 'inactive') { teardownRecorder(); return; }

        const duration = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
        const blob = await new Promise((resolve) => {
            recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: recorder.mimeType }));
            try { recorder.stop(); } catch (_) { resolve(new Blob()); }
        });
        teardownRecorder();
        if (!blob || blob.size === 0) return;

        const id = `seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const blobUrl = URL.createObjectURL(blob);
        setSegments((prev) => [...prev, {
            id,
            blob,
            blobUrl,
            duration,
            mimeType: blob.type || 'audio/webm',
            status: 'transcribing',
            audioUrl: null,
            gcsPath: null,
            transcription: null,
            // Edit Box (#273): COLLAPSED by default. #173 shipped this expanded so
            // the pre-send editable transcription was one click closer, but forcing
            // the text into view clutters the composer — the entry point for EVERY
            // new recording is now the "Show transcription" toggle, exactly like the
            // Main Office (#252). The toggle flips it per segment; a fresh recording
            // always starts collapsed again because this initializer runs per clip.
            showTranscription: false,
            error: null,
        }]);

        // Background transcription — never blocks the UI.
        (async () => {
            try {
                const result = await documentService.uploadAndTranscribeAudio(
                    blob, conversationId,
                );
                setSegments((prev) => prev.map((s) => (s.id === id ? {
                    ...s,
                    status: 'ready',
                    audioUrl: documentService.getAudioStreamUrl(result.audio_url),
                    gcsPath: result.gcs_path,
                    transcription: result.transcription || '[inaudible]',
                    mimeType: result.mime_type || s.mimeType,
                } : s)));
            } catch (err) {
                logger.error('VOICE', 'Segment transcription failed:', err);
                setSegments((prev) => prev.map((s) => (s.id === id ? {
                    ...s, status: 'failed', error: userErrorMessage(err),
                } : s)));
            }
        })();
        // `conversationId` is in the deps so a bucket switch can't leave the
        // closure attributing a clip to the conversation that was open before it.
    }, [teardownRecorder, conversationId]);

    const remove = useCallback((segmentId) => {
        setSegments((prev) => {
            const target = prev.find((s) => s.id === segmentId);
            if (target?.blobUrl) { try { URL.revokeObjectURL(target.blobUrl); } catch (_) { /* ignore */ } }
            return prev.filter((s) => s.id !== segmentId);
        });
    }, []);

    const toggleTranscription = useCallback((segmentId) => {
        setSegments((prev) => prev.map((s) => (s.id === segmentId
            ? { ...s, showTranscription: !s.showTranscription } : s)));
    }, []);

    /** Pre-send transcription editing — the text the turn actually runs on. */
    const editTranscription = useCallback((segmentId, text) => {
        setSegments((prev) => prev.map((s) => (s.id === segmentId
            ? { ...s, transcription: text } : s)));
    }, []);

    /** Clear the tray after a send (blob URLs are revoked on a delay so the
     *  optimistic bubble can still play them while server URLs settle). */
    const clear = useCallback(() => {
        setSegments((prev) => {
            const urls = prev.map((s) => s.blobUrl).filter(Boolean);
            setTimeout(() => {
                urls.forEach((u) => { try { URL.revokeObjectURL(u); } catch (_) { /* ignore */ } });
            }, 5000);
            return [];
        });
    }, []);

    // Unmount: stop any live capture and free every outstanding blob URL.
    useEffect(() => () => {
        if (timerRef.current) clearInterval(timerRef.current);
        const rec = recorderRef.current;
        if (rec && rec.state !== 'inactive') { try { rec.stop(); } catch (_) { /* ignore */ } }
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        segmentsRef.current.forEach((s) => {
            if (s.blobUrl) { try { URL.revokeObjectURL(s.blobUrl); } catch (_) { /* ignore */ } }
        });
    }, []);

    const hasTranscribing = segments.some((s) => s.status === 'transcribing');

    return {
        segments,
        isRecording,
        recordingDuration,
        hasTranscribing,
        start,
        accept,
        discard,
        remove,
        toggleTranscription,
        editTranscription,
        clear,
    };
}

export default useComposerVoiceSegments;
